import { useEffect, useState } from 'react';
import axios from 'axios';
import { Card, CardContent, CardHeader, CardTitle } from './ui/card';
import { Badge } from './ui/badge';
import { Button } from './ui/button';
import { Select, SelectTrigger, SelectValue, SelectContent, SelectItem } from './ui/select';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from './ui/table';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from './ui/dialog';
import { Textarea } from './ui/textarea';
import { Award, MapPin, AlertTriangle, MailCheck, RefreshCcw } from 'lucide-react';
import { toast } from 'sonner';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const authHeader = () => ({ Authorization: `Bearer ${localStorage.getItem('token')}` });

function uptimeTone(pct) {
  if (pct == null) return 'bg-slate-100 text-slate-500';
  if (pct >= 95) return 'bg-emerald-100 text-emerald-700 border-emerald-300';
  if (pct >= 80) return 'bg-amber-100 text-amber-700 border-amber-300';
  return 'bg-red-100 text-red-700 border-red-300';
}

export function TravelCompliancePanel() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [days, setDays] = useState(7);
  const [checkInWorker, setCheckInWorker] = useState(null);
  const [checkInMessage, setCheckInMessage] = useState('');
  const [sending, setSending] = useState(false);

  const load = async (n = days) => {
    setLoading(true);
    try {
      const res = await axios.get(`${API}/admin/travel-compliance?days=${n}`, { headers: authHeader() });
      setData(res.data);
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed to load compliance');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(days); }, [days]);

  const openCheckIn = (row) => {
    setCheckInWorker(row);
    setCheckInMessage(
      `Hi ${row.worker_name.split(' ')[0]}, your travel-tracking uptime over the last ${days} days is ${row.uptime_pct}%. ` +
      `Could you make sure Location is enabled on your device before your next shift? It only takes a sec and it makes a big difference for NDIS claims. Thanks!`
    );
  };

  const sendCheckIn = async () => {
    if (!checkInWorker) return;
    setSending(true);
    try {
      await axios.post(`${API}/admin/travel-compliance/check-in`,
        { worker_id: checkInWorker.worker_id, message: checkInMessage },
        { headers: authHeader() });
      toast.success(`Check-in sent to ${checkInWorker.worker_name}`);
      setCheckInWorker(null);
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed to send check-in');
    } finally {
      setSending(false);
    }
  };

  return (
    <Card className="bg-slate-800 border-slate-700" data-testid="travel-compliance-panel">
      <CardHeader className="flex flex-col sm:flex-row sm:items-center justify-between gap-3 pb-3">
        <div className="min-w-0">
          <CardTitle className="text-white flex items-center gap-2 flex-wrap">
            <Award className="h-5 w-5 text-amber-400" />
            Travel-Tracking Compliance
            {data && (
              <Badge variant="outline" className="ml-1 border-slate-600 text-slate-200">
                <MapPin className="h-3 w-3 mr-1" />
                {data.summary.org_uptime_pct == null ? '—' : `${data.summary.org_uptime_pct}%`} org uptime
              </Badge>
            )}
            {data?.summary?.needs_attention > 0 && (
              <Badge variant="outline" className="border-red-500/40 text-red-300 bg-red-500/10">
                <AlertTriangle className="h-3 w-3 mr-1" />
                {data.summary.needs_attention} need{data.summary.needs_attention !== 1 ? '' : 's'} attention
              </Badge>
            )}
          </CardTitle>
          <p className="text-xs text-slate-400 mt-1">
            % of completed shifts with at least one GPS ping. Lower = travel KMs missing from NDIS claims.
          </p>
        </div>
        <div className="flex items-center gap-2 shrink-0">
          <Select value={String(days)} onValueChange={(v) => setDays(parseInt(v))}>
            <SelectTrigger className="h-9 w-28 bg-slate-700 border-slate-600 text-white" data-testid="compliance-days">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="7">7 days</SelectItem>
              <SelectItem value="14">14 days</SelectItem>
              <SelectItem value="30">30 days</SelectItem>
              <SelectItem value="90">90 days</SelectItem>
            </SelectContent>
          </Select>
          <Button
            variant="outline"
            size="sm"
            onClick={() => load(days)}
            disabled={loading}
            className="border-slate-700 text-slate-200 hover:bg-slate-700"
            data-testid="compliance-refresh"
          >
            <RefreshCcw className={`h-3.5 w-3.5 ${loading ? 'animate-spin' : ''}`} />
          </Button>
        </div>
      </CardHeader>
      <CardContent className="pt-0">
        {!data ? (
          <div className="py-8 text-center text-slate-400">Loading…</div>
        ) : (data.rows || []).filter(r => r.shifts > 0).length === 0 ? (
          <div className="py-8 text-center text-slate-400">No completed shifts in this window.</div>
        ) : (
          <div className="overflow-x-auto">
            <Table>
              <TableHeader>
                <TableRow className="border-slate-700 hover:bg-transparent">
                  <TableHead className="text-slate-400">Worker</TableHead>
                  <TableHead className="text-slate-400 text-right">Shifts</TableHead>
                  <TableHead className="text-slate-400 text-right">Tracked</TableHead>
                  <TableHead className="text-slate-400 text-right">KMs</TableHead>
                  <TableHead className="text-slate-400 text-right">Uptime</TableHead>
                  <TableHead className="text-slate-400"></TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {data.rows.filter(r => r.shifts > 0).map((r) => (
                  <TableRow key={r.worker_id} className="border-slate-700" data-testid={`compliance-row-${r.worker_id}`}>
                    <TableCell className="text-white font-medium">{r.worker_name}</TableCell>
                    <TableCell className="text-right text-slate-300 tabular-nums">{r.shifts}</TableCell>
                    <TableCell className="text-right text-slate-300 tabular-nums">{r.tracked_shifts}</TableCell>
                    <TableCell className="text-right text-slate-300 tabular-nums">{r.total_km}</TableCell>
                    <TableCell className="text-right">
                      <Badge variant="outline" className={`tabular-nums ${uptimeTone(r.uptime_pct)}`} data-testid={`compliance-uptime-${r.worker_id}`}>
                        {r.uptime_pct == null ? '—' : `${r.uptime_pct}%`}
                      </Badge>
                    </TableCell>
                    <TableCell>
                      {r.uptime_pct != null && r.uptime_pct < 80 && (
                        <Button
                          size="sm"
                          variant="outline"
                          onClick={() => openCheckIn(r)}
                          className="h-7 px-2 text-xs border-amber-500/50 text-amber-300 hover:bg-amber-500/10"
                          data-testid={`compliance-checkin-${r.worker_id}`}
                        >
                          <MailCheck className="h-3 w-3 mr-1" />
                          Check in
                        </Button>
                      )}
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </div>
        )}
      </CardContent>

      <Dialog open={!!checkInWorker} onOpenChange={(o) => !o && setCheckInWorker(null)}>
        <DialogContent data-testid="compliance-checkin-dialog">
          <DialogHeader>
            <DialogTitle>Send check-in to {checkInWorker?.worker_name}</DialogTitle>
            <p className="text-sm text-slate-500">
              Sends a friendly in-app notification reminding the worker to keep their device's Location enabled.
            </p>
          </DialogHeader>
          <Textarea
            rows={5}
            value={checkInMessage}
            onChange={(e) => setCheckInMessage(e.target.value)}
            data-testid="compliance-checkin-message"
          />
          <DialogFooter>
            <Button variant="outline" onClick={() => setCheckInWorker(null)} disabled={sending}>Cancel</Button>
            <Button onClick={sendCheckIn} disabled={sending || !checkInMessage.trim()} className="bg-teal-600 hover:bg-teal-700" data-testid="compliance-checkin-send">
              {sending ? 'Sending…' : 'Send'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </Card>
  );
}
